import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  Icon,
  IconButton,
  Kbd,
  SearchInput,
  Sheet,
  Tag,
  ToneIcon,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkTone } from "@NP-Webber/crm-kit/dist/next/index.js";
import { EVENTS, onEvent } from "../events";
import { useShell } from "../shell/ShellContext";
import { isActivePath } from "../shell/navConfig";
import { DesignOptions } from "../design/DesignOptions";
import { hubSection } from "../settings/settingsSections";
import { RELATED_SETTINGS, rulesFor } from "./relatedCatalog";
import { matchesQuery, resolveSetting, SETTINGS_HUB_PATH, type ResolvedSetting } from "./resolveSetting";
import {
  pushRecent,
  readRecent,
  readReturn,
  useScreenSettingsList,
  writeReturn,
  type RelatedReturn,
  type ScreenSettingItem,
} from "./relatedStore";
import "./related.css";

const SHORTCUT = "Ctrl+,";
const GROUP_TONES: CkTone[] = ["primary", "success", "info", "violet", "warning", "neutral"];

/** "הגדרות קשורות" in the top bar — opens the panel with the settings that shape the current screen. */
export function RelatedSettingsButton() {
  const label = `הגדרות קשורות למסך הזה (${SHORTCUT})`;
  return (
    <button
      type="button"
      className="rs-btn"
      onClick={() => window.dispatchEvent(new CustomEvent(EVENTS.related))}
      aria-label={label}
      title={label}
    >
      <Icon name="sliders" size={16} />
      <span className="rs-btn__label">הגדרות קשורות</span>
    </button>
  );
}

/**
 * RelatedSettings — mounted once by the shell. Owns the Ctrl+, shortcut, the side panel,
 * opening + highlighting the target card after a jump, and the "חזרה ל…" pill.
 */
export function RelatedSettings() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);

  useEffect(() => {
    const off = onEvent(EVENTS.related, () => setOpen(true));
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "," || e.code === "Comma")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      off();
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) setWide(false);
  }, [open]);

  return (
    <>
      <Sheet open={open} onClose={() => setOpen(false)} width={wide ? 880 : 480} ariaLabel="הגדרות קשורות" className="rs-sheet">
        <RelatedSettingsBody open={open} onClose={() => setOpen(false)} onWide={setWide} />
      </Sheet>
      <FocusOnArrival />
      <ReturnPill />
    </>
  );
}

function groupTone(group: string): CkTone {
  const names = Array.from(new Set(RELATED_SETTINGS.map((s) => s.group)));
  return GROUP_TONES[Math.max(0, names.indexOf(group)) % GROUP_TONES.length];
}

function RelatedSettingsBody({ open, onClose, onWide }: { open: boolean; onClose: () => void; onWide: (wide: boolean) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { nav, current } = useShell();
  const screenItems = useScreenSettingsList();
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState<ResolvedSetting | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [recent, setRecent] = useState<string[]>(() => readRecent());

  useEffect(() => {
    if (!open) return;
    setQ("");
    setDetail(null);
    setRecent(readRecent());
  }, [open, location.pathname]);
  useEffect(() => onWide(Boolean(detail) && !detail?.target.design), [detail, onWide]);

  const available = useMemo(
    () => RELATED_SETTINGS.map((s) => resolveSetting(s, nav)).filter((s): s is ResolvedSetting => s !== null),
    [nav],
  );
  const byId = useMemo(() => new Map(available.map((s) => [s.id, s])), [available]);

  const rule = rulesFor(location.pathname);
  const related = (rule?.items ?? [])
    .map(([id, why]): ResolvedSetting | null => {
      const s = byId.get(id);
      return s ? { ...s, why } : null;
    })
    .filter((s): s is ResolvedSetting => s !== null)
    // the screen you are on is not "related" to itself
    .filter((s) => !(s.path && isActivePath(location.pathname, s.path) && !s.query));
  const relatedIds = new Set(related.map((s) => s.id));
  const recentItems = recent
    .map((id) => byId.get(id))
    .filter((s): s is ResolvedSetting => Boolean(s) && !relatedIds.has((s as ResolvedSetting).id))
    .slice(0, 3);
  const rest = available.filter((s) => !relatedIds.has(s.id) && !s.target.design);

  const query = q.trim();
  const results = query
    ? {
        screen: screenItems.filter((i) => matchesQuery(`${i.title} ${i.description || ""}`, query)),
        settings: available.filter((s) =>
          matchesQuery(`${s.title} ${s.description} ${s.keywords || ""} ${s.group} ${s.where}`, query),
        ),
      }
    : null;

  const go = (s: ResolvedSetting, full = false) => {
    pushRecent(s.id);
    if (s.inline && !full) {
      setDetail(s);
      return;
    }
    if (!s.path) return;
    if (!isActivePath(location.pathname, s.path)) {
      writeReturn({ from: location.pathname + location.search, label: current?.label || "", to: s.path, setting: s.title });
    }
    onClose();
    navigate(`${s.path}${s.query ? `?${s.query}` : ""}`, { state: s.focus ? { relatedFocus: s.focus } : undefined });
  };
  const runScreenItem = (i: ScreenSettingItem) => {
    onClose();
    window.setTimeout(() => i.onClick(), 160);
  };
  const onSearchKey = (e: ReactKeyboardEvent) => {
    if (e.key !== "Enter" || !results) return;
    if (results.screen[0]) runScreenItem(results.screen[0]);
    else if (results.settings[0]) go(results.settings[0]);
  };

  if (detail) {
    return <InlineDetail s={detail} onBack={() => setDetail(null)} onClose={onClose} onFull={() => go(detail, true)} />;
  }

  const card = (s: ResolvedSetting, showGroup = false) => (
    <SettingCard key={s.id} s={s} showGroup={showGroup} onOpen={() => go(s)} onFull={s.inline && s.path ? () => go(s, true) : undefined} />
  );
  const grouped = groupBy(rest);
  const count = results ? results.screen.length + results.settings.length : 0;

  return (
    <div className="rs">
      <header className="rs__head">
        <span className="rs__logo">
          <Icon name="sliders" size={19} />
        </span>
        <div className="rs__titles">
          <b>הגדרות קשורות</b>
          <small>
            {current ? (
              <>
                למסך <span className="rs__here">«{current.label}»</span>
              </>
            ) : (
              "הגדרות המערכת"
            )}
          </small>
        </div>
        <IconButton icon="x" label="סגירה" onClick={onClose} />
      </header>

      <div className="rs__search" onKeyDown={onSearchKey}>
        <SearchInput value={q} onChange={setQ} placeholder="חיפוש בכל ההגדרות…" autoFocus />
      </div>

      <div className="rs__body">
        {results ? (
          count ? (
            <section className="rs__sec">
              <h3 className="rs__sec-title">{count === 1 ? "תוצאה אחת" : `${count} תוצאות`}</h3>
              <div className="rs__list">
                {results.screen.map((i) => (
                  <ScreenItemCard key={`screen-${i.key}`} i={i} onOpen={() => runScreenItem(i)} />
                ))}
                {results.settings.map((s) => card(s, true))}
              </div>
            </section>
          ) : (
            <EmptyState icon="search" title="לא נמצאה הגדרה">
              נסו מילה אחרת — למשל «קבלות», «נדרים» או «הרשאות»
            </EmptyState>
          )
        ) : (
          <>
            {screenItems.length ? (
              <section className="rs__sec">
                <h3 className="rs__sec-title">
                  <Icon name="pin" size={14} />
                  בתוך המסך הזה
                </h3>
                <div className="rs__list">
                  {screenItems.map((i) => (
                    <ScreenItemCard key={i.key} i={i} onOpen={() => runScreenItem(i)} />
                  ))}
                </div>
              </section>
            ) : null}

            <section className="rs__sec">
              <h3 className="rs__sec-title">
                <Icon name="sparkle" size={14} />
                {rule ? "משפיעות על המסך הזה" : "הגדרות נפוצות"}
              </h3>
              {related.length ? (
                <div className="rs__list">{related.map((s) => card(s))}</div>
              ) : (
                <div className="rs__none">
                  אין הגדרות שקשורות במיוחד למסך הזה — אפשר לחפש למעלה או לפתוח את כל ההגדרות.
                </div>
              )}
            </section>

            {recentItems.length ? (
              <section className="rs__sec">
                <h3 className="rs__sec-title">
                  <Icon name="history" size={14} />
                  נפתחו לאחרונה
                </h3>
                <div className="rs__chips">
                  {recentItems.map((s) => (
                    <button key={s.id} type="button" className="rs__chip" onClick={() => go(s)}>
                      <Icon name={s.icon || "gear"} size={14} />
                      {s.title}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {rest.length ? (
              <section className="rs__sec">
                <button type="button" className="rs__all-toggle" aria-expanded={showAll} onClick={() => setShowAll((v) => !v)}>
                  <Icon name="grid" size={14} />
                  <span className="rs__all-label">כל ההגדרות</span>
                  <Tag>{rest.length}</Tag>
                  <Icon name={showAll ? "up" : "down"} size={14} className="rs__all-caret" />
                </button>
                {showAll
                  ? grouped.map(([g, items]) => (
                      <div key={g} className="rs__group">
                        <div className="rs__gname">{g}</div>
                        {items.map((s) => (
                          <button key={s.id} type="button" className="rs__row" onClick={() => go(s)}>
                            <Icon name={s.icon || "gear"} size={15} />
                            <span className="rs__row-title">{s.title}</span>
                            <Icon name={s.inline ? "edit" : "left"} size={13} className="rs__row-go" />
                          </button>
                        ))}
                      </div>
                    ))
                  : null}
              </section>
            ) : null}

            <section className="rs__sec rs__look">
              <h3 className="rs__sec-title">
                <Icon name="sun" size={14} />
                מראה ותצוגה
              </h3>
              <DesignOptions compact />
            </section>
          </>
        )}
      </div>

      <footer className="rs__foot">
        <small>
          <Kbd>Ctrl</Kbd>+<Kbd>,</Kbd> פותח מכל מסך
        </small>
        {nav.some((n) => n.key === "settings") ? (
          <Button
            size="sm"
            variant="ghost"
            icon="gear"
            onClick={() => {
              onClose();
              navigate(SETTINGS_HUB_PATH);
            }}
          >
            כל ההגדרות
          </Button>
        ) : null}
      </footer>
    </div>
  );
}

function groupBy(items: ResolvedSetting[]): Array<[string, ResolvedSetting[]]> {
  const out = new Map<string, ResolvedSetting[]>();
  items.forEach((s) => {
    if (!out.has(s.group)) out.set(s.group, []);
    out.get(s.group)?.push(s);
  });
  return Array.from(out.entries());
}

function SettingCard({
  s,
  onOpen,
  onFull,
  showGroup = false,
}: {
  s: ResolvedSetting;
  onOpen: () => void;
  onFull?: () => void;
  showGroup?: boolean;
}) {
  return (
    <div className="rs-card">
      <button type="button" className="rs-card__main" onClick={onOpen}>
        <ToneIcon icon={s.icon || "gear"} tone={groupTone(s.group)} size={34} />
        <span className="rs-card__text">
          <span className="rs-card__title">{s.title}</span>
          <span className="rs-card__why">{s.why || s.description}</span>
          {showGroup || !s.inline ? (
            <span className="rs-card__where">
              {showGroup ? <span>{s.group}</span> : null}
              {!s.inline ? (
                <>
                  <Icon name="left" size={12} />
                  {s.where}
                </>
              ) : null}
            </span>
          ) : null}
        </span>
        {s.inline ? (
          <span className="rs-card__here" title="נערך כאן, בלי לעזוב את המסך">
            <Icon name="edit" size={13} />
            כאן
          </span>
        ) : (
          <Icon name="left" size={15} className="rs-card__go" />
        )}
      </button>
      {onFull ? <IconButton size="sm" icon="external" label="פתיחה במסך ההגדרות" onClick={onFull} className="rs-card__full" /> : null}
    </div>
  );
}

function ScreenItemCard({ i, onOpen }: { i: ScreenSettingItem; onOpen: () => void }) {
  return (
    <div className="rs-card is-screen">
      <button type="button" className="rs-card__main" onClick={onOpen}>
        <ToneIcon icon={i.icon || "gear"} tone="primary" size={34} />
        <span className="rs-card__text">
          <span className="rs-card__title">{i.title}</span>
          {i.description ? <span className="rs-card__why">{i.description}</span> : null}
          <span className="rs-card__where">
            <Icon name="pin" size={12} />
            נפתח כאן במסך
          </span>
        </span>
        <Icon name="left" size={15} className="rs-card__go" />
      </button>
    </div>
  );
}

/** a settings-hub section, edited right inside the panel */
function InlineDetail({ s, onBack, onClose, onFull }: { s: ResolvedSetting; onBack: () => void; onClose: () => void; onFull: () => void }) {
  const section = s.target.hub ? hubSection(s.target.hub) : undefined;
  return (
    <div className="rs is-detail">
      <header className="rs__head">
        <IconButton icon="right" label="חזרה להגדרות הקשורות" onClick={onBack} />
        <ToneIcon icon={s.icon || "gear"} tone={groupTone(s.group)} size={34} />
        <div className="rs__titles">
          <b>{s.title}</b>
          <small>{section?.description || s.description}</small>
        </div>
        {s.path ? <IconButton icon="external" label="פתיחה במסך ההגדרות" onClick={onFull} /> : null}
        <IconButton icon="x" label="סגירה" onClick={onClose} />
      </header>
      {s.why ? (
        <div className="rs__why">
          <Icon name="info" size={14} />
          {s.why}
        </div>
      ) : null}
      <div className="rs__body rs__inline">
        {s.target.design ? <DesignOptions /> : section?.render ? section.render() : <EmptyState icon="gear" title="ההגדרה לא זמינה כרגע" />}
      </div>
      <footer className="rs__foot">
        <small>נשמר באותו מקום כמו במסך ההגדרות</small>
        <Button size="sm" variant="ghost" icon="right" onClick={onBack}>
          לכל ההגדרות הקשורות
        </Button>
      </footer>
    </div>
  );
}

/* ── after a jump: open + highlight the card named in location.state.relatedFocus ── */
const normalize = (s: string | null | undefined) => String(s || "").replace(/\s+/g, " ").trim();

function findCard(title: string): HTMLElement | null {
  const want = normalize(title);
  const heads = document.querySelectorAll<HTMLElement>(
    ".ck-panel__toggle, .ck-deck__head, .ck-settings__item, [aria-expanded], [role='tab']",
  );
  for (const el of Array.from(heads)) {
    if (normalize(el.textContent).startsWith(want)) return el;
  }
  return null;
}

function FocusOnArrival() {
  const location = useLocation();
  const handled = useRef<string | null>(null);
  useEffect(() => {
    const title = (location.state as { relatedFocus?: string } | null)?.relatedFocus;
    if (!title || handled.current === location.key) return undefined;
    handled.current = location.key;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const head = findCard(title);
      if (!head && tries < 40) return;
      window.clearInterval(timer);
      if (!head) return;
      if (head.getAttribute("aria-expanded") === "false" || head.getAttribute("role") === "tab") head.click();
      const card = (head.closest(".ck-panel, .ck-deck__item, .ck-settings__item") as HTMLElement | null) || head;
      card.scrollIntoView({ behavior: "smooth", block: "start" });
      card.classList.add("rs-flash");
      window.setTimeout(() => card.classList.remove("rs-flash"), 2600);
    }, 150);
    return () => window.clearInterval(timer);
  }, [location.key, location.state]);
  return null;
}

/* ── "חזרה ל«…»" — after jumping to a setting, one click back to the screen you came from ── */
function ReturnPill() {
  const location = useLocation();
  const navigate = useNavigate();
  const [ret, setRet] = useState<RelatedReturn | null>(() => readReturn());
  useEffect(() => {
    const cur = readReturn();
    if (!cur) {
      setRet(null);
      return;
    }
    if (!isActivePath(location.pathname, cur.to)) {
      writeReturn(null);
      setRet(null);
      return;
    }
    setRet(cur);
  }, [location.pathname]);
  if (!ret) return null;
  const dismiss = () => {
    writeReturn(null);
    setRet(null);
  };
  return (
    <div className="rs-return" role="status">
      <button
        type="button"
        className="rs-return__go"
        onClick={() => {
          dismiss();
          navigate(ret.from);
        }}
      >
        <Icon name="right" size={15} />
        <span>
          חזרה ל<b>{ret.label ? `«${ret.label}»` : "מסך הקודם"}</b>
        </span>
      </button>
      {ret.setting ? <small className="rs-return__what">{ret.setting}</small> : null}
      <button type="button" className="rs-return__x" aria-label="סגירה" onClick={dismiss}>
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}

export default RelatedSettings;
